import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import {
  dimensionRowLimitSelector,
  realTimeModeOnTrendsSelector,
  getFirstAppliedFilter,
} from '@store/common/selectors';
import { filterModeAtom } from '@src/atoms/page';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import { formatAppData } from '@components/dimension-panel-hz/dimension-table/utils';
import { chartIntervalSelector, noBrushSelector } from '@store/trends/birdChart/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { getPercentageMetric } from '@utils/metrics/metricHelpers';
import { formatColumns, formatMultiColumns, formatMultiData } from './formatColumn';
import { dimensionTraceId, networkMetrics } from './constant';
import { drillDownFiltersAtom } from './atom';
import { useQueryGroupBy } from './hooks';

export function notEqualUnknownFilter(field: string) {
  return [
    {
      field,
      key: null,
      op: 'not_eq',
      value: 'CONVIVA_INTERNAL_UNKNOWN',
      display: 'UNKNOWN',
      originalField: field,
    },
  ];
}
export function eventNameFilter() {
  return [
    {
      display: 'conviva_network_request',
      field: 'event_name',
      key: null,
      op: 'eq',
      originalField: 'event_name',
      value: 'conviva_network_request',
      defaultFilter: true,
    },
  ];
}

function filter4TraceId(field: string, rule) {
  if (!field) {
    return rule;
  }
  return Array.isArray(rule)
    ? [...rule, notEqualUnknownFilter(field), eventNameFilter()]
    : [notEqualUnknownFilter(field), eventNameFilter()];
}
function useAppDimension({ dimension, unit, enabled = true }) {
  const filter = useSelector(getFirstAppliedFilter);
  const interval = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const sortByMetric = networkMetrics[1];
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const appFilterMode = useAtomValue(filterModeAtom);
  const dimensionByTypes: any = useSelector(dimensionByTypesSelector);
  const drillDownFilters = useAtomValue(drillDownFiltersAtom);
  const inSecondaryFilter = metricFilter && metricFilter.length > 0;

  const noBrushSelected = useSelector(noBrushSelector);
  const { limit } = useSelector(dimensionRowLimitSelector);
  const [sortBy, setSortBy] = useState({ key: sortByMetric, order: 'desc' as const });
  const onColumnSort = ({ key, order }) => {
    setSortBy({ key, order });
  };
  const [searchText, onSearch] = useState('');
  const isDimensionTraceId = dimensionTraceId === dimension;

  // update local sort when selected or order by metric changes
  useEffect(() => {
    onColumnSort({ key: sortByMetric, order: 'desc' });
  }, [sortByMetric]);

  const fixedFilter = {
    ...filter,
    /** Do not append not-equal filter when inSecondaryFilter, as the filter should placed in drillDownFilter */
    rule:
      !inSecondaryFilter && isDimensionTraceId
        ? filter4TraceId(dimension, filter.rule)
        : filter.rule,
  };
  const {
    isFetching,
    isError,
    data = {},
    refetch,
  } = useQueryGroupBy({
    dimension,
    metrics: dimension === dimensionTraceId ? [] : networkMetrics,
    filter: fixedFilter,
    interval,
    enabled,
    metricFilter,
    sortByMetric: dimension === dimensionTraceId ? null : sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    /** only for sf */
    useSamplingForEventAnalysis: inSecondaryFilter,
    samplingAsDataset: inSecondaryFilter && dimension === dimensionTraceId,
    drillDownFilters: inSecondaryFilter
      ? isDimensionTraceId
        ? filter4TraceId(dimension, drillDownFilters)
        : drillDownFilters
      : undefined,
  });

  const { adjustedInterval }: any = data;

  let rows = [];
  let totals = {};
  let columns: any[] = [];

  // unit based metrics
  const columnMetrics = (dimension === dimensionTraceId ? [] : networkMetrics).map((metric) => {
    return unit === '%' ? getPercentageMetric(metric) : metric;
  });

  const currentDimensionType = dimensionByTypes[dimension];

  if (!isEmpty(dimensionByTypes)) {
    if ('metricFilterData' in data) {
      ({ rows, totals } = formatMultiData({ data, sortBy, searchText }));
      columns = formatMultiColumns({
        dimension,
        metrics: columnMetrics,
        showNAForMetric: null,
      });
    } else {
      ({ rows, totals } = formatAppData({
        data,
        sortBy,
        appFilterMode,
        searchText,
        columnMetrics,
        currentDimensionType,
      }));
      columns = formatColumns({
        dimension,
        metrics: columnMetrics,
        showNAForMetric: null,
      });
    }
  }

  return {
    isFetching,
    isError,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals,
    data: rows,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    adjustedInterval,
  };
}

export default useAppDimension;
